<template>
  <div class="agent-config-form">
    <div class="agent-form-head">
      <div>
        <span class="agent-form-kicker">{{ modeLabel }}</span>
        <h4>{{ config.header.title || "Pengaturan agent" }}</h4>
      </div>
      <span class="agent-form-status" :class="config.header.is_publish == 1 ? 'is-active' : 'is-draft'">
        {{ config.header.is_publish == 1 ? "Aktif" : "Draft" }}
      </span>
    </div>

    <div v-if="errors && errors.length" class="agent-form-errors">
      <div v-for="(error, index) in errors" :key="index">
        <i class="fa fa-exclamation-circle"></i> {{ error }}
      </div>
    </div>

    <div class="agent-form-grid">
      <div class="agent-form-main">
        <div class="form-group">
          <label>Judul Pengaturan <span class="text-danger">*</span></label>
          <input
            v-model="config.header.title"
            type="text"
            class="form-control"
            maxlength="50"
            placeholder="Contoh: Balasan utama pelanggan"
            :data-agent-field="fieldAttr('title')"
          />
          <small v-if="fieldErrors.title" class="agent-field-error">{{ fieldErrors.title }}</small>
          <small class="agent-helper">{{ (config.header.title || "").length }}/50 karakter</small>
        </div>

        <div class="form-group">
          <label>Pesan Respons <span class="text-danger">*</span></label>
          <div :data-agent-field="fieldAttr('message')">
            <chat-component v-model="config.header.message" />
          </div>
          <small class="agent-helper" :class="{ 'is-danger': messageLength > 1024 }">
            {{ messageLength }}/1024 karakter
          </small>
          <small v-if="fieldErrors.message" class="agent-field-error">{{ fieldErrors.message }}</small>
        </div>

        <div class="agent-form-single">
          <div class="form-group">
            <label class="agent-label-with-info">
              Frekuensi per Orang
              <info-tooltip
                text="Frekuensi per Orang adalah batas seberapa sering satu orang bisa menerima balasan otomatis yang sama. Kalau pelanggan mengirim pesan berkali-kali dalam waktu singkat, balasan otomatis tidak dikirim berulang-ulang. Chat jadi lebih rapi dan tidak terasa seperti spam."
                aria-label="Penjelasan Frekuensi per Orang"
              />
            </label>
            <select
              v-model.number="config.header.frequency_per_person"
              class="form-control"
              :data-agent-field="fieldAttr('frequency')"
            >
              <option v-for="option in frequencyOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
            <small v-if="fieldErrors.frequency" class="agent-field-error">{{ fieldErrors.frequency }}</small>
          </div>
        </div>

        <div class="agent-section">
          <div class="agent-section-head">
            <div class="agent-section-copy">
              <h5><i class="fa fa-paperclip"></i> Lampiran</h5>
              <p>Kirim gambar atau dokumen bersama pesan. Lampiran dinonaktifkan saat Chat AI aktif.</p>
            </div>
            <toggle-switch
              class="agent-section-switch"
              :value="!!config.header.is_enable_attach_file"
              :disabled="aiActive"
              aria-label="Lampiran"
              :data-agent-field="fieldAttr('attachment')"
              @input="onAttachToggle"
            />
          </div>
          <div v-if="aiActive" class="agent-inline-note">
            <i class="fa fa-info-circle"></i>
            Lampiran tidak dapat digunakan saat Chat AI aktif. Pilih Chatbot terlebih dahulu.
          </div>
          <small v-if="fieldErrors.attachment" class="agent-field-error">{{ fieldErrors.attachment }}</small>

          <div v-if="config.header.is_enable_attach_file && !aiActive" class="agent-upload-box">
            <div class="agent-attachment-type" role="group" aria-label="Jenis lampiran">
              <button
                v-for="option in attachmentTypeOptions"
                :key="option.value"
                type="button"
                class="agent-attachment-type-btn"
                :class="{ active: attachmentType === option.value }"
                @click="setAttachmentType(option.value)"
              >
                <i :class="option.icon"></i>
                {{ option.label }}
              </button>
            </div>

            <div v-if="config.header.image_url" class="agent-attachment-current">
              <div class="agent-attachment-thumb">
                <img
                  v-if="attachmentType === 'image' && !attachmentThumbError"
                  :src="config.header.image_url"
                  alt="Lampiran"
                  @error="attachmentThumbError = true"
                />
                <i
                  v-else
                  :class="attachmentType === 'image' ? 'fa fa-image' : 'fa fa-file-text-o'"
                ></i>
              </div>
              <div class="agent-attachment-meta">
                <strong :title="attachmentDisplayName">{{ attachmentDisplayName }}</strong>
                <span>
                  {{ attachmentDisplayExt || attachmentTypeLabel }}
                  <template v-if="attachmentDisplaySize"> · {{ attachmentDisplaySize }}</template>
                </span>
              </div>
              <div class="agent-attachment-actions">
                <button
                  type="button"
                  class="agent-attachment-action"
                  @click="showUploadPicker = !showUploadPicker"
                >
                  <i class="fa fa-refresh"></i> Ganti
                </button>
                <button
                  type="button"
                  class="agent-attachment-action is-danger"
                  @click="removeAttachment"
                >
                  <i class="fa fa-trash-o"></i> Hapus
                </button>
              </div>
            </div>

            <div v-show="!config.header.image_url || showUploadPicker" class="agent-upload-picker">
              <file-upload
                :key="fileUploadKey"
                :ref="uploadRef"
                class="btn btn-outline-secondary"
                :post-action="uploadUrl"
                :headers="uploadHeaders"
                :data="uploadData"
                :multiple="false"
                :maximum="1"
                :accept="attachmentAccept"
                :extensions="attachmentExtensions"
                v-model="uploadFiles"
                @input-filter="inputFilter"
                @input-file="inputFile"
              >
                <i class="fa fa-upload"></i>
                {{ config.header.image_url ? "Pilih file pengganti" : "Pilih file" }}
              </file-upload>
              <small class="agent-helper">
                <template v-if="attachmentType === 'image'">
                  Format: JPG, JPEG, PNG, WEBP, GIF.
                </template>
                <template v-else>
                  Format: PDF, DOC, DOCX, XLS, XLSX, TXT, ZIP, PPTX.
                </template>
                Minimal 1 KB, maksimal 10 MB.
              </small>
              <div v-if="uploadError" class="agent-upload-error">{{ uploadError }}</div>
            </div>
          </div>
        </div>

        <div class="agent-section agent-ai-section">
          <div class="agent-section-head">
            <div class="agent-section-copy">
              <h5><i class="fa fa-magic"></i> Jenis Agent</h5>
              <p>Chatbot menjawab dengan aturan dan pesan yang Anda siapkan. Chat AI menjawab dengan bantuan AI.</p>
            </div>
            <toggle-switch
              class="agent-section-switch"
              :value="aiEnabled"
              aria-label="Jenis Agent"
              @input="onAiToggle"
            />
          </div>

          <agent-type-choice
            :value="config.header.ai_status"
            :data-agent-field="fieldAttr('agentType')"
            @input="setAgentType"
          />
          <small v-if="fieldErrors.agentType" class="agent-field-error">{{ fieldErrors.agentType }}</small>

          <template v-if="isChatbot">
            <text-reply-picker
              v-if="!hasLegacyButtonData"
              ref="textReplyPicker"
              :value="config.detail_button"
              :options="textReplyRows"
              :loading="textReplyLoading"
              :error="textReplyError"
              :max="textReplyMax"
              :data-agent-field="fieldAttr('textReply')"
              @input="onTextReplyInput"
              @create="$emit('create-text-reply', $event)"
              @reload="$emit('reload-text-reply')"
            />
            <div v-else class="agent-text-reply-legacy">
              <i class="fa fa-info-circle"></i>
              <div>
                <strong>Text Reply tidak ditampilkan</strong>
                <span>
                  Konfigurasi ini masih memakai data tombol/link lama. Data lama tidak diubah agar tetap kompatibel.
                </span>
              </div>
            </div>
          </template>
          <small v-if="fieldErrors.textReply" class="agent-field-error">{{ fieldErrors.textReply }}</small>

          <template v-if="config.header.ai_status == 1">
            <prompt-template-picker v-model="config.header.ai_prompt_system" />

            <div class="form-group">
              <label>Prompt AI</label>
              <textarea
                v-model="config.header.ai_prompt_system"
                class="form-control"
                rows="5"
                placeholder="Contoh: Kamu adalah asisten perusahaan yang ramah dan menjawab berdasarkan informasi yang tersedia."
                :data-agent-field="fieldAttr('aiPrompt')"
              ></textarea>
              <small v-if="fieldErrors.aiPrompt" class="agent-field-error">{{ fieldErrors.aiPrompt }}</small>
            </div>

            <div class="agent-ai-mode" :data-agent-field="fieldAttr('aiMode')">
              <button
                type="button"
                class="agent-ai-mode-card"
                :class="{ active: config.header.ai_type === 'freestyle' }"
                @click="config.header.ai_type = 'freestyle'"
              >
                <span class="agent-radio"></span>
                <strong>Chat Completions</strong>
                <small>AI menjawab dengan pengetahuan umum dan prompt Anda.</small>
              </button>
              <button
                type="button"
                class="agent-ai-mode-card"
                :class="{ active: config.header.ai_type === 'summarize' }"
                @click="config.header.ai_type = 'summarize'"
              >
                <span class="agent-radio"></span>
                <strong>Knowledge Base</strong>
                <small>AI menjawab berdasarkan dokumen KB lisensi ini.</small>
              </button>
            </div>
            <small v-if="fieldErrors.aiMode" class="agent-field-error">{{ fieldErrors.aiMode }}</small>

            <div
              v-if="config.header.ai_type === 'summarize'"
              class="agent-kb-box"
              :data-agent-field="fieldAttr('knowledgeBase')"
            >
              <div class="agent-kb-head">
                <div>
                  <strong>Knowledge Base Lisensi</strong>
                  <small>Dokumen Knowledge Base digunakan bersama oleh semua agent AI pada lisensi ini.</small>
                </div>
                <agent-button
                  variant="primary"
                  size="sm"
                  icon="fa fa-book"
                  @click="$emit('open-kb')"
                >
                  Kelola Knowledge Base
                </agent-button>
              </div>
              <small v-if="fieldErrors.knowledgeBase" class="agent-field-error">{{ fieldErrors.knowledgeBase }}</small>
              <div v-if="kbLoading" class="agent-kb-state">
                <i class="fa fa-spinner fa-spin"></i> Memuat Knowledge Base...
              </div>
              <div v-else-if="kbError" class="agent-kb-state is-error">
                {{ kbError }}
                <button type="button" class="btn btn-link" @click="$emit('reload-kb')">Coba lagi</button>
              </div>
              <div v-else>
                <div class="agent-kb-count">
                  <strong>{{ kbTotal }} dari {{ kbLimit }} KB</strong>
                  <span v-if="kbTotal >= kbLimit" class="agent-kb-warning">Batas 5 KB tercapai</span>
                </div>
                <div v-if="kbRows.length" class="agent-kb-list">
                  <div v-for="row in kbRows" :key="row.id" class="agent-kb-item">
                    <i class="fa fa-file-text-o"></i>
                    <span>{{ row.file_name }}</span>
                    <small :class="'is-' + (row.status || 'pending')">{{ kbStatusLabel(row.status) }}</small>
                  </div>
                </div>
                <div v-else class="agent-kb-empty">
                  Belum ada Knowledge Base untuk lisensi ini. Tambahkan dokumen agar AI dapat menggunakannya.
                </div>
              </div>
            </div>

            <div class="agent-form-two">
              <div class="form-group">
                <label class="agent-label-with-info">
                  Tutup Sesi Jika Tidak Ada Interaksi
                  <info-tooltip
                    text="Jika tidak ada interaksi selama waktu yang ditentukan, sesi akan otomatis ditutup. Jika pelanggan mengirim pesan lagi setelah sesi ditutup, percakapan dianggap sebagai sesi baru dan akan diawali dengan Pesan Respons."
                    aria-label="Penjelasan Tutup Sesi"
                  />
                </label>
                <select
                  v-model.number="config.header.ai_close_frequency"
                  class="form-control"
                  :data-agent-field="fieldAttr('session')"
                >
                  <option v-for="option in closeFrequencyOptions" :key="option.value" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>
              </div>
              <div class="form-group agent-session-options">
                <label>Pengaturan Sesi</label>
                <toggle-row v-model="aiLoading" label="Tampilkan Loading AI" />
                <toggle-row v-model="aiSummary" label="Buat Rangkuman Saat Sesi Selesai" />
                <toggle-row v-model="aiClose" label="Sampaikan Pesan Saat Sesi Berakhir" />
              </div>
            </div>
            <small v-if="fieldErrors.session" class="agent-field-error">{{ fieldErrors.session }}</small>

            <div v-if="aiClose" class="form-group">
              <label>Pesan Akhir Sesi</label>
              <textarea
                v-model="config.header.ai_close_remarks"
                class="form-control"
                rows="3"
                placeholder="Pesan yang dikirim saat sesi AI berakhir"
              ></textarea>
            </div>
          </template>
        </div>
      </div>

      <aside class="agent-form-preview">
        <div class="agent-preview-label">Pratinjau Pesan</div>
        <div class="agent-preview-phone">
          <div class="agent-preview-chat-date"><span>Hari ini</span></div>
          <div class="agent-preview-bubble">
            <div
              v-if="previewAttachment"
              class="agent-preview-attachment"
              :class="'is-' + previewAttachment.type"
            >
              <template v-if="previewAttachment.type === 'image'">
                <img
                  v-if="previewAttachment.hasFile && !previewImageError"
                  :src="previewAttachment.url"
                  alt="Lampiran gambar"
                  @error="previewImageError = true"
                />
                <div v-else class="agent-preview-file-placeholder">
                  <i class="fa fa-image"></i>
                  <span>
                    {{ previewAttachment.hasFile ? "Gambar belum dapat ditampilkan" : "Gambar belum dipilih" }}
                  </span>
                </div>
              </template>
              <template v-else>
                <div
                  class="agent-preview-doc-card"
                  :class="{ 'is-empty': !previewAttachment.hasFile }"
                >
                  <i class="fa fa-file-text-o"></i>
                  <div class="agent-preview-doc-meta">
                    <strong :title="previewAttachment.name">{{ previewAttachment.name }}</strong>
                    <span v-if="previewAttachment.hasFile">
                      {{ previewAttachment.ext || "DOKUMEN" }}
                      <template v-if="previewAttachment.sizeText"> · {{ previewAttachment.sizeText }}</template>
                    </span>
                    <span v-else>Pilih file dokumen</span>
                  </div>
                </div>
              </template>
            </div>
            <div
              v-if="formattedMessage"
              class="agent-preview-message"
              v-html="formattedMessage"
            ></div>
            <div v-else class="agent-preview-empty">
              Pesan respons akan tampil di sini.
            </div>
            <div v-if="showPreviewTextReplies" class="agent-preview-replies">
              <div
                v-for="(item, index) in previewTextReplies"
                :key="item.id + '-' + index"
              >
                <span>{{ index + 1 }}.</span>
                <span>{{ item.title }}</span>
              </div>
            </div>
            <div v-if="aiActive" class="agent-preview-ai-note">
              <i class="fa fa-magic"></i>
              <span>Dijawab oleh Chat AI · {{ aiModeLabel }}</span>
            </div>
            <div class="agent-preview-time">
              08:50
              <i class="fa fa-check"></i>
            </div>
          </div>
        </div>
        <div class="agent-preview-meta">
          <div><span>Jenis Agent</span><strong>{{ config.header.ai_status == 1 ? "Chat AI" : "Chatbot" }}</strong></div>
          <div v-if="config.header.ai_status == 1">
            <span>Mode AI</span><strong>{{ aiModeLabel }}</strong>
          </div>
          <div><span>Frekuensi</span><strong>{{ frequencyLabel }}</strong></div>
        </div>
        <div class="agent-preview-variable-note">
          Contoh data variabel: Jojo · 6281234567890
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import FileUpload from "vue-upload-component";
import ChatComponent from "@/views/broadcast/componentsV2/Forms/chatComponent.vue";
import ToggleRow from "./ToggleRow.vue";
import ToggleSwitch from "./ToggleSwitch.vue";
import InfoTooltip from "./InfoTooltip.vue";
import AgentButton from "./AgentButton.vue";
import AgentTypeChoice from "./AgentTypeChoice.vue";
import PromptTemplatePicker from "./PromptTemplatePicker.vue";
import TextReplyPicker from "./TextReplyPicker.vue";
import { APICore } from "@/utils/servicesApi";
import { getXAuthToken } from "@/utils/auth";
import { formatSizes } from "@/utils/helper";
import {
  FREQUENCY_OPTIONS,
  CLOSE_FREQUENCY_OPTIONS,
  MAX_TEXT_REPLY,
  ATTACHMENT_MAX_SIZE,
  ATTACHMENT_MIN_SIZE,
  IMAGE_EXTENSIONS,
  DOCUMENT_EXTENSIONS,
  escapeHtml,
  fileExtension,
  fileNameFromUrl
} from "../utils";

export default {
  name: "AgentConfigForm",
  components: {
    FileUpload,
    ChatComponent,
    ToggleRow,
    ToggleSwitch,
    InfoTooltip,
    AgentButton,
    AgentTypeChoice,
    PromptTemplatePicker,
    TextReplyPicker
  },
  props: {
    config: {
      type: Object,
      required: true
    },
    modeLabel: {
      type: String,
      default: ""
    },
    formKey: {
      type: String,
      default: ""
    },
    licenseId: {
      type: String,
      default: ""
    },
    errors: {
      type: Array,
      default: () => []
    },
    fieldErrors: {
      type: Object,
      default: () => ({})
    },
    kbRows: {
      type: Array,
      default: () => []
    },
    kbTotal: {
      type: Number,
      default: 0
    },
    kbLimit: {
      type: Number,
      default: 5
    },
    kbLoading: {
      type: Boolean,
      default: false
    },
    kbError: {
      type: String,
      default: ""
    },
    textReplyRows: {
      type: Array,
      default: () => []
    },
    textReplyLoading: {
      type: Boolean,
      default: false
    },
    textReplyError: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      frequencyOptions: FREQUENCY_OPTIONS,
      closeFrequencyOptions: CLOSE_FREQUENCY_OPTIONS,
      textReplyMax: MAX_TEXT_REPLY,
      attachmentType: "image",
      attachmentTypeOptions: [
        { value: "image", label: "Image", icon: "fa fa-image" },
        { value: "document", label: "Document", icon: "fa fa-file-text-o" }
      ],
      attachmentName: "",
      attachmentSize: 0,
      attachmentMime: "",
      attachmentExt: "",
      attachmentMetaUrl: "",
      attachmentThumbError: false,
      previewImageError: false,
      showUploadPicker: false,
      uploadFiles: [],
      uploadData: {
        message_type: "image",
        license_id: ""
      },
      uploadError: "",
      uploadUrl: process.env.VUE_APP_BASE_API + APICore() + "/omni/only-upload-filev2"
    };
  },
  computed: {
    uploadHeaders() {
      return {
        "X-Auth-Token": "Bearer " + getXAuthToken()
      };
    },
    uploadRef() {
      const key = this.config.header.id || (
        String(this.config.header.is_working_or_away) + "_" + String(this.config.header.is_working)
      );
      return "agentUpload_" + key;
    },
    attachmentAccept() {
      if (this.attachmentType === "image") {
        return "image/jpeg,image/png,image/webp,image/gif";
      }
      return [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        "text/plain",
        "application/zip",
        "application/octet-stream"
      ].join(",");
    },
    attachmentExtensions() {
      return (this.attachmentType === "image" ? IMAGE_EXTENSIONS : DOCUMENT_EXTENSIONS).join(",");
    },
    attachmentTypeLabel() {
      return this.attachmentType === "document" ? "Dokumen" : "Gambar";
    },
    attachmentDisplayName() {
      if (this.attachmentName) return this.attachmentName;
      const url = this.config.header.image_url || "";
      if (url) return fileNameFromUrl(url) || "Lampiran tersimpan";
      return this.attachmentType === "document" ? "Dokumen belum dipilih" : "Gambar belum dipilih";
    },
    attachmentDisplayExt() {
      const ext = this.attachmentExt || fileExtension(this.config.header.image_url);
      return ext ? ext.toUpperCase() : "";
    },
    attachmentDisplaySize() {
      return this.attachmentSize > 0 ? formatSizes(this.attachmentSize) : "";
    },
    previewAttachment() {
      if (this.aiActive || !this.config.header.is_enable_attach_file) return null;
      const type = this.attachmentType === "document" ? "document" : "image";
      const url = this.config.header.image_url || "";
      return {
        type: type,
        url: url,
        hasFile: !!url,
        name: this.attachmentDisplayName,
        ext: this.attachmentDisplayExt,
        sizeText: this.attachmentDisplaySize
      };
    },
    fileUploadKey() {
      return this.uploadRef + "_" + this.attachmentType + "_" + (this.licenseId || this.config.header.license_id || "");
    },
    messageLength() {
      return (this.config.header.message || "").length;
    },
    frequencyLabel() {
      const found = FREQUENCY_OPTIONS.find(
        item => Number(item.value) === Number(this.config.header.frequency_per_person)
      );
      return found ? found.label : "-";
    },
    aiActive() {
      return Number(this.config.header.ai_status) === 1;
    },
    isChatbot() {
      return !this.aiActive;
    },
    hasLegacyButtonData() {
      const buttonType = this.config.header.button_type;
      return (
        buttonType &&
        buttonType !== "text_reply" &&
        (this.config.detail_button || []).some(row => row && row.auto_reply_button_id)
      );
    },
    aiModeLabel() {
      return this.config.header.ai_type === "summarize" ? "Knowledge Base" : "Chat Completions";
    },
    formattedMessage() {
      const raw = this.replacePreviewVariables(this.config.header.message || "");
      if (!raw) return "";
      const escaped = escapeHtml(raw);
      if (this.$util && typeof this.$util.formatMessage === "function") {
        return this.$util.formatMessage(escaped);
      }
      return escaped.replace(/\r?\n/g, "<br />");
    },
    previewTextReplies() {
      if (this.aiActive || this.config.header.button_type !== "text_reply") return [];
      return (this.config.detail_button || [])
        .filter(row => row && row.auto_reply_button_id)
        .map(row => {
          const id = String(row.auto_reply_button_id);
          const found = (this.textReplyRows || []).find(
            item => String(item.id) === id
          );
          return {
            id: id,
            title: found
              ? found.title || found.button_title || found.button_text || "Text Reply"
              : "Text Reply tersimpan"
          };
        });
    },
    showPreviewTextReplies() {
      return !this.aiActive && this.previewTextReplies.length > 0;
    },
    aiEnabled: {
      get() {
        return Number(this.config.header.ai_status) === 1;
      },
      set(value) {
        this.config.header.ai_status = value ? 1 : 0;
      }
    },
    aiLoading: {
      get() {
        return Number(this.config.header.ai_loading) === 1;
      },
      set(value) {
        this.config.header.ai_loading = value ? 1 : 0;
      }
    },
    aiSummary: {
      get() {
        return Number(this.config.header.is_ai_create_summary) === 1;
      },
      set(value) {
        this.config.header.is_ai_create_summary = value ? 1 : 0;
      }
    },
    aiClose: {
      get() {
        return Number(this.config.header.ai_close_status) === 1;
      },
      set(value) {
        this.config.header.ai_close_status = value ? 1 : 0;
      }
    }
  },
  watch: {
    config: {
      immediate: true,
      handler() {
        this.syncAttachmentState();
      }
    },
    licenseId(value) {
      this.uploadData.license_id = value || (this.config.header && this.config.header.license_id) || "";
    },
    "config.header.image_url"(value) {
      this.previewImageError = false;
      this.attachmentThumbError = false;
      if (value) {
        if (value !== this.attachmentMetaUrl) {
          this.loadAttachmentMeta(value, this.attachmentType);
        }
      } else {
        this.resetAttachmentMeta();
      }
    },
    "config.header.message_type"(value) {
      if (value !== "image" && value !== "document") return;
      this.attachmentType = value;
      this.uploadData.message_type = value;
    },
    "config.header.is_enable_attach_file"(value) {
      if (value) return;
      this.uploadFiles = [];
      this.showUploadPicker = false;
      this.uploadError = "";
      this.resetAttachmentMeta();
      this.clearUploader();
    }
  },
  methods: {
    fieldAttr(name) {
      return this.formKey ? this.formKey + ":" + name : name;
    },
    syncAttachmentState() {
      const header = this.config.header || {};
      const type = header.message_type === "document" ? "document" : "image";
      this.attachmentType = type;
      if (
        header.is_enable_attach_file &&
        header.message_type !== "image" &&
        header.message_type !== "document"
      ) {
        header.message_type = type;
      }
      this.uploadData.message_type = type;
      this.uploadData.license_id = this.licenseId || header.license_id || "";
      const url = header.image_url || "";
      if (url) {
        if (url !== this.attachmentMetaUrl) this.loadAttachmentMeta(url, type);
      } else {
        this.resetAttachmentMeta();
      }
      this.showUploadPicker = false;
      this.uploadError = "";
      this.previewImageError = false;
      this.attachmentThumbError = false;
    },
    loadAttachmentMeta(url, type) {
      this.attachmentMetaUrl = url || "";
      this.attachmentName =
        fileNameFromUrl(url) ||
        (type === "document" ? "Lampiran dokumen" : "Lampiran gambar");
      this.attachmentSize = 0;
      this.attachmentMime = "";
      this.attachmentExt = fileExtension(url);
    },
    resetAttachmentMeta() {
      this.attachmentName = "";
      this.attachmentSize = 0;
      this.attachmentMime = "";
      this.attachmentExt = "";
      this.attachmentMetaUrl = "";
      this.attachmentThumbError = false;
      this.previewImageError = false;
    },
    clearUploader() {
      const upload = this.$refs[this.uploadRef];
      if (!upload) return;
      if (typeof upload.clear === "function") {
        upload.clear();
      } else if (typeof upload.remove === "function") {
        upload.remove();
      }
      this.uploadFiles = [];
    },
    clearAttachmentFile() {
      this.config.header.image_url = "";
      this.uploadFiles = [];
      this.showUploadPicker = false;
      this.uploadError = "";
      this.resetAttachmentMeta();
      this.clearUploader();
    },
    clearAttachmentForAi() {
      this.config.header.is_enable_attach_file = false;
      this.config.header.image_url = "";
      this.config.header.message_type = "text";
      this.uploadFiles = [];
      this.showUploadPicker = false;
      this.uploadError = "";
      this.resetAttachmentMeta();
      this.clearUploader();
    },
    attachmentValidationMessage(file, type) {
      const name = String((file && file.name) || "");
      const mime = String((file && file.type) || "").toLowerCase();
      const ext = fileExtension(name);
      if (!ext) return "Format file tidak dikenali.";
      if (/^(php5?|html?|jsx?|exe|sh)$/i.test(ext)) {
        return "Format file tersebut tidak diizinkan.";
      }
      if (type === "image") {
        if (IMAGE_EXTENSIONS.indexOf(ext) === -1) {
          return "Format gambar harus JPG, JPEG, PNG, WEBP, atau GIF.";
        }
        if (mime && mime.indexOf("image/") !== 0) {
          return "File yang dipilih bukan gambar yang valid.";
        }
      } else {
        if (DOCUMENT_EXTENSIONS.indexOf(ext) === -1) {
          return "Format dokumen harus PDF, DOC, DOCX, XLS, XLSX, TXT, ZIP, atau PPTX.";
        }
        if (mime && (mime.indexOf("image/") === 0 || mime.indexOf("video/") === 0)) {
          return "File yang dipilih bukan dokumen yang valid.";
        }
      }
      return "";
    },
    fileMatchesType(file, type) {
      const name = String((file && (file.name || file.url)) || "");
      const mime = String((file && file.mime) || "").toLowerCase();
      const ext = fileExtension(name);
      if (type === "image") {
        if (mime && mime.indexOf("image/") !== 0) return false;
        return IMAGE_EXTENSIONS.indexOf(ext) !== -1;
      }
      if (mime && (mime.indexOf("image/") === 0 || mime.indexOf("video/") === 0)) {
        return false;
      }
      return DOCUMENT_EXTENSIONS.indexOf(ext) !== -1;
    },
    setAttachmentType(type) {
      if (type !== "image" && type !== "document") return;
      this.uploadError = "";
      const previousUrl = this.config.header.image_url || "";
      const previousFile = {
        url: previousUrl,
        name: this.attachmentName || fileNameFromUrl(previousUrl),
        mime: this.attachmentMime
      };

      this.attachmentType = type;
      this.uploadData.message_type = type;
      this.uploadData.license_id = this.licenseId || this.config.header.license_id || "";
      if (this.config.header.is_enable_attach_file) {
        this.config.header.message_type = type;
      }
      this.previewImageError = false;
      this.attachmentThumbError = false;

      if (previousUrl && !this.fileMatchesType(previousFile, type)) {
        this.clearAttachmentFile();
        this.uploadError =
          type === "image"
            ? "File sebelumnya bukan gambar. Silakan pilih file gambar baru."
            : "File sebelumnya bukan dokumen. Silakan pilih file dokumen baru.";
      }
    },
    friendlyUploadError(message) {
      const raw = String(message || "").toLowerCase();
      if (raw.indexOf("ukuran") !== -1 || raw.indexOf("size") !== -1) {
        return "Ukuran file lampiran maksimal 10 MB.";
      }
      if (
        raw.indexOf("hanya mengizinkan file") !== -1 ||
        raw.indexOf("format file") !== -1 ||
        raw.indexOf("mime") !== -1 ||
        raw.indexOf("tidak sesuai") !== -1
      ) {
        return "Format file tidak sesuai dengan jenis media yang dipilih.";
      }
      if (raw.indexOf("lisensi") !== -1 || raw.indexOf("license") !== -1) {
        return "Lisensi tidak ditemukan atau bukan milik Anda.";
      }
      if (raw.indexOf("sesi") !== -1 || raw.indexOf("unauthorized") !== -1) {
        return "Sesi Anda sudah berakhir. Silakan masuk kembali.";
      }
      return message || "Lampiran belum berhasil diunggah. Silakan coba lagi.";
    },
    replacePreviewVariables(message) {
      const samples = {
        first_name: "Jojo",
        last_name: "Pratama",
        phone_number: "6281234567890",
        email: "jojo@example.com",
        month_current: "10",
        month_current_name: "Oktober",
        year_current: "2026",
        today: "29",
        yesterday: "28",
        month_ago: "9",
        month_ago_name: "September",
        year_ago: "2025"
      };
      return String(message || "").replace(
        /\$\{([a-zA-Z0-9_]+)\}/g,
        (match, variableName) => {
          return samples[variableName] !== undefined
            ? samples[variableName]
            : "[" + variableName + "]";
        }
      );
    },
    selectTextReplyOption(index, option) {
      if (!option || !option.id) return;
      const picker = this.$refs.textReplyPicker;
      if (picker && typeof picker.selectOption === "function") {
        picker.selectOption(index, option);
        return;
      }
      const rows = (this.config.detail_button || []).slice();
      rows[index] = {
        auto_reply_button_id: String(option.id),
        button_type: "text_reply"
      };
      this.config.detail_button = rows.filter(row => row && row.auto_reply_button_id);
      this.config.header.button_type = "text_reply";
    },
    kbStatusLabel(status) {
      if (status === "success") return "Selesai";
      if (status === "processing") return "Sedang diproses";
      if (status === "failed") return "Gagal";
      if (status === "pending") return "Menunggu";
      return status || "Menunggu";
    },
    setAgentType(type) {
      this.config.header.ai_status = type;
      if (type === 1) {
        this.clearAttachmentForAi();
        if (!this.config.header.id || !this.config.header.ai_type) this.config.header.ai_type = "summarize";
      } else {
        this.config.header.ai_close_status = 0;
        this.config.header.ai_loading = 0;
        this.config.header.is_ai_create_summary = 0;
      }
    },
    onAiToggle(value) {
      this.config.header.ai_status = value ? 1 : 0;
      if (value) {
        this.clearAttachmentForAi();
        if (!this.config.header.id || !this.config.header.ai_type) {
          this.config.header.ai_type = "summarize";
        }
      } else {
        this.config.header.ai_close_status = 0;
        this.config.header.ai_loading = 0;
        this.config.header.is_ai_create_summary = 0;
      }
    },
    onAttachToggle(value) {
      this.config.header.is_enable_attach_file = !!value;
      if (!value) {
        this.config.header.image_url = "";
        this.config.header.message_type = "text";
        this.showUploadPicker = false;
        this.uploadError = "";
        this.uploadFiles = [];
        this.resetAttachmentMeta();
        this.clearUploader();
        return;
      }
      this.attachmentType = this.attachmentType === "document" ? "document" : "image";
      this.config.header.message_type = this.attachmentType;
      this.uploadData.message_type = this.attachmentType;
      this.uploadData.license_id = this.licenseId || this.config.header.license_id || "";
      this.showUploadPicker = !this.config.header.image_url;
    },
    onTextReplyInput(rows) {
      this.config.detail_button = rows;
      if ((rows || []).some(row => row && row.auto_reply_button_id)) {
        this.config.header.button_type = "text_reply";
      }
    },
    inputFilter(newFile, oldFile, prevent) {
      if (newFile && !oldFile) {
        if (Number(this.config.header.ai_status) === 1) {
          this.uploadError = "Nonaktifkan Chat AI terlebih dahulu untuk mengunggah lampiran.";
          return prevent();
        }
        if (newFile.size > ATTACHMENT_MAX_SIZE) {
          this.uploadError = "Ukuran file maksimal 10 MB.";
          return prevent();
        }
        if (newFile.size < ATTACHMENT_MIN_SIZE) {
          this.uploadError = "Ukuran file minimal 1 KB.";
          return prevent();
        }
        const validation = this.attachmentValidationMessage(
          { name: newFile.name, type: newFile.type },
          this.attachmentType
        );
        if (validation) {
          this.uploadError = validation;
          return prevent();
        }
        this.uploadData.message_type = this.attachmentType;
        this.uploadData.license_id = this.licenseId || this.config.header.license_id || "";
        this.uploadError = "";
      }
    },
    inputFile(newFile, oldFile) {
      if (newFile && !oldFile) {
        const upload = this.$refs[this.uploadRef];
        if (upload) {
          this.$nextTick(() => {
            upload.active = true;
          });
        }
      }
      if (newFile && oldFile) {
        if (newFile.active && !oldFile.active) {
          const data = {
            message_type: this.attachmentType,
            license_id: this.licenseId || this.config.header.license_id || ""
          };
          this.uploadData.message_type = data.message_type;
          this.uploadData.license_id = data.license_id;
          const upload = this.$refs[this.uploadRef];
          if (upload && typeof upload.update === "function") {
            upload.update(newFile, { data: data });
          }
        }
        if (newFile.error && !oldFile.error) {
          this.uploadError = this.friendlyUploadError(
            (newFile.response && (newFile.response.msg || newFile.response.message)) ||
              newFile.error
          );
        }
        if (newFile.success && !oldFile.success) {
          const uploader = this.$refs[this.uploadRef];
          const response = newFile.response || {};
          const data = response.data || {};
          const url = data.url || data.path || "";
          if (!url) {
            this.uploadError = "Upload selesai tetapi URL lampiran tidak diterima server.";
            return;
          }
          const type = data.message_type === "document" ? "document" : "image";
          this.attachmentType = type;
          this.config.header.image_url = url;
          this.config.header.is_enable_attach_file = true;
          this.config.header.message_type = type;
          this.attachmentName = newFile.name || data.filename || fileNameFromUrl(url);
          this.attachmentSize = Number(newFile.size) || 0;
          this.attachmentMime = data.file_type || newFile.type || "";
          this.attachmentExt = String(data.ext || fileExtension(this.attachmentName || url)).replace(/^\./, "");
          this.attachmentMetaUrl = url;
          this.uploadData.message_type = type;
          this.uploadData.license_id = this.licenseId || this.config.header.license_id || "";
          this.uploadError = "";
          this.showUploadPicker = false;
          this.attachmentThumbError = false;
          this.previewImageError = false;
          this.$nextTick(() => {
            if (uploader && typeof uploader.clear === "function") uploader.clear();
            this.uploadFiles = [];
          });
        }
      }
    },
    removeAttachment() {
      this.clearAttachmentFile();
      if (this.config.header.is_enable_attach_file) {
        this.config.header.message_type = this.attachmentType;
      }
      this.showUploadPicker = false;
      this.uploadError = "";
    }
  }
};
</script>

<style scoped>
.agent-config-form {
  border: 1px solid #e7eaf0;
  border-radius: 16px;
  background: #fff;
  padding: 20px;
}
.agent-config-form .form-group > label {
  margin-bottom: 6px;
  color: #344054;
  font-size: 13px;
  font-weight: 700;
}
.agent-config-form .form-control {
  font-size: 13px;
}
.agent-config-form .form-control:focus {
  border-color: #fe9b44;
  box-shadow: 0 0 0 3px rgba(254, 155, 68, .12);
}
.agent-form-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}
.agent-form-kicker {
  display: inline-block;
  color: #e77f25;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
}
.agent-form-head h4 {
  margin: 4px 0 0;
  color: #172033;
  font-size: 18px;
  font-weight: 800;
}
.agent-form-status {
  border-radius: 999px;
  padding: 5px 10px;
  font-size: 12px;
  font-weight: 800;
}
.agent-form-status.is-active {
  background: #e7f8ef;
  color: #0b8f4d;
}
.agent-form-status.is-draft {
  background: #fff4e5;
  color: #b76e00;
}
.agent-form-errors {
  margin-bottom: 14px;
  border: 1px solid #ffd6d6;
  border-radius: 10px;
  background: #fff7f7;
  color: #c02626;
  padding: 10px 12px;
  font-size: 12px;
  line-height: 1.7;
}
.agent-form-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 260px;
  gap: 20px;
}
.agent-form-main {
  min-width: 0;
}
.agent-form-two {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}
.agent-form-single {
  max-width: 360px;
}
.agent-label-with-info {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.agent-session-options {
  border: 1px solid #edf1f6;
  border-radius: 10px;
  background: #fbfcfe;
  padding: 7px 11px;
}
.agent-session-options > label:first-child {
  margin-bottom: 3px;
}
.agent-helper {
  display: block;
  margin-top: 4px;
  color: #98a2b3;
  font-size: 12px;
}
.agent-helper.is-danger {
  color: #d92d20;
}
.agent-field-error {
  display: block;
  margin-top: 4px;
  color: #d92d20;
  font-size: 12px;
  line-height: 1.4;
}
.agent-inline-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
}
.agent-inline-toggle .form-control {
  min-width: 0;
}
.agent-section {
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid #f0f2f6;
}
.agent-section-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
}
.agent-section-copy {
  min-width: 0;
}
.agent-section-switch {
  margin-top: -2px;
}
.agent-section-head h5 {
  margin: 0 0 3px;
  color: #344054;
  font-size: 14px;
  font-weight: 800;
}
.agent-section-head p {
  margin: 0;
  color: #98a2b3;
  font-size: 12px;
  line-height: 1.5;
}
.agent-inline-note {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  margin: -2px 0 10px;
  border-radius: 9px;
  background: #f7f9fc;
  color: #667085;
  padding: 8px 10px;
  font-size: 12px;
  line-height: 1.5;
}
.agent-inline-note i {
  margin-top: 2px;
  color: #2e90fa;
}
.agent-text-reply-legacy {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 12px 0;
  border: 1px solid #e9eef5;
  border-radius: 10px;
  background: #fbfcfe;
  color: #667085;
  padding: 11px 12px;
  font-size: 12px;
  line-height: 1.5;
}
.agent-text-reply-legacy > i {
  margin-top: 2px;
  color: #b76e00;
}
.agent-text-reply-legacy strong {
  display: block;
  color: #344054;
  font-size: 12px;
}
.agent-text-reply-legacy span {
  display: block;
  margin-top: 2px;
}
.agent-upload-box {
  border: 1px dashed #d8dee9;
  border-radius: 12px;
  padding: 12px;
  background: #fbfcfe;
}
.agent-attachment-type {
  display: inline-flex;
  gap: 4px;
  margin-bottom: 12px;
  border: 1px solid #e7eaf0;
  border-radius: 9px;
  background: #fff;
  padding: 3px;
}
.agent-attachment-type-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 92px;
  justify-content: center;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: #667085;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: background .15s ease, color .15s ease, box-shadow .15s ease;
}
.agent-attachment-type-btn:hover {
  color: #b45309;
  background: #fffaf3;
}
.agent-attachment-type-btn.active {
  color: #b45309;
  background: #fff3e3;
  box-shadow: inset 0 0 0 1px #f7c98b;
}
.agent-attachment-current {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  border: 1px solid #edf1f6;
  border-radius: 10px;
  background: #fff;
  padding: 9px;
}
.agent-attachment-thumb {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 48px;
  width: 48px;
  height: 48px;
  overflow: hidden;
  border-radius: 8px;
  background: #f4f7fb;
  color: #b45309;
  font-size: 20px;
}
.agent-attachment-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.agent-attachment-meta {
  flex: 1 1 auto;
  min-width: 0;
}
.agent-attachment-meta strong {
  display: block;
  overflow: hidden;
  color: #344054;
  font-size: 12px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.agent-attachment-meta span {
  display: block;
  margin-top: 2px;
  color: #98a2b3;
  font-size: 12px;
}
.agent-attachment-actions {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  gap: 5px;
}
.agent-attachment-action {
  border: 1px solid #e4e8ef;
  border-radius: 7px;
  background: #fff;
  color: #667085;
  padding: 5px 8px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}
.agent-attachment-action:hover {
  border-color: #f7c98b;
  color: #b45309;
  background: #fffaf3;
}
.agent-attachment-action.is-danger {
  color: #d92d20;
}
.agent-upload-picker {
  margin-top: 10px;
}
.agent-upload-error {
  margin-top: 6px;
  color: #d92d20;
  font-size: 12px;
  line-height: 1.45;
}
.agent-ai-mode {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}
.agent-ai-mode-card {
  text-align: left;
  border: 1px solid #e7eaf0;
  border-radius: 12px;
  background: #fff;
  padding: 14px;
  cursor: pointer;
}
.agent-ai-mode-card strong {
  display: block;
  color: #344054;
  font-size: 13px;
}
.agent-ai-mode-card small {
  display: block;
  margin-top: 3px;
  color: #98a2b3;
  font-size: 12px;
  line-height: 1.4;
}
.agent-ai-mode-card.active {
  border-color: #fe9b44;
  background: #fff8f1;
}
.agent-radio {
  display: inline-block;
  width: 14px;
  height: 14px;
  margin-bottom: 7px;
  border: 2px solid #cbd5e1;
  border-radius: 50%;
}
.agent-ai-mode-card.active .agent-radio {
  border-color: #fe9b44;
  box-shadow: inset 0 0 0 3px #fff;
  background: #fe9b44;
}
.agent-kb-box {
  margin: 14px 0;
  border: 1px solid #e9eef5;
  border-radius: 12px;
  background: #f8fbff;
  padding: 14px;
}
.agent-kb-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.agent-kb-head strong {
  display: block;
  color: #344054;
  font-size: 13px;
}
.agent-kb-head small {
  display: block;
  margin-top: 3px;
  color: #7b8ba3;
  font-size: 12px;
}
.agent-kb-state,
.agent-kb-empty {
  margin-top: 10px;
  color: #7b8ba3;
  font-size: 12px;
}
.agent-kb-state.is-error {
  color: #d92d20;
}
.agent-kb-count {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  color: #344054;
  font-size: 12px;
}
.agent-kb-warning {
  color: #b76e00;
  font-size: 12px;
}
.agent-kb-list {
  margin-top: 8px;
}
.agent-kb-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 0;
  border-top: 1px solid #edf2f7;
  color: #475467;
  font-size: 12px;
}
.agent-kb-item span {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
}
.agent-kb-item small {
  border-radius: 999px;
  padding: 2px 7px;
  background: #fff4e5;
  color: #b76e00;
  font-size: 12px;
  text-transform: capitalize;
}
.agent-kb-item small.is-success {
  background: #e7f8ef;
  color: #0b8f4d;
}
.agent-kb-item small.is-processing {
  background: #e8f2ff;
  color: #1769d1;
}
.agent-kb-item small.is-failed {
  background: #fff0f0;
  color: #c02626;
}
.agent-toggle-line {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  color: #475467;
  font-size: 12px;
}
.agent-form-preview {
  align-self: start;
  border: 1px solid #e9eef5;
  border-radius: 14px;
  background: #fbfcfe;
  padding: 14px;
}
.agent-preview-label {
  margin-bottom: 10px;
  color: #98a2b3;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
}
.agent-preview-phone {
  position: relative;
  overflow: hidden;
  border-radius: 14px;
  background-color: #efeae2;
  background-image: radial-gradient(rgba(17, 27, 33, .035) 1px, transparent 1px);
  background-size: 14px 14px;
  padding: 14px 12px 18px;
  min-height: 220px;
}
.agent-preview-chat-date {
  margin-bottom: 10px;
  text-align: center;
}
.agent-preview-chat-date span {
  display: inline-block;
  border-radius: 6px;
  background: #fff;
  color: #667781;
  padding: 3px 8px;
  font-size: 12px;
  font-weight: 700;
  box-shadow: 0 1px 1px rgba(11, 20, 26, .08);
}
.agent-preview-bubble {
  position: relative;
  margin-left: auto;
  max-width: 90%;
  border-radius: 10px 10px 2px 10px;
  background: #d9fdd3;
  padding: 9px 10px 7px;
  color: #111b21;
  font-size: 12.5px;
  line-height: 1.48;
  overflow-wrap: anywhere;
  word-break: break-word;
  box-shadow: 0 1px .5px rgba(11, 20, 26, .13);
}
.agent-preview-bubble:after {
  position: absolute;
  right: -6px;
  bottom: 0;
  content: "";
  border-top: 7px solid transparent;
  border-left: 7px solid #d9fdd3;
}
.agent-preview-message {
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.agent-preview-message >>> b,
.agent-preview-message >>> strong {
  font-weight: 800;
}
.agent-preview-message >>> i,
.agent-preview-message >>> em {
  font-style: italic;
}
.agent-preview-message >>> s,
.agent-preview-message >>> strike {
  text-decoration: line-through;
}
.agent-preview-message >>> pre {
  margin: 6px 0;
  border-radius: 6px;
  background: rgba(255, 255, 255, .72);
  padding: 6px 7px;
  color: #111b21;
  font-family: Consolas, Monaco, monospace;
  font-size: 12px;
  line-height: 1.45;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.agent-preview-empty {
  color: #667781;
  font-style: italic;
}
.agent-preview-replies {
  margin-top: 7px;
  border-top: 1px solid rgba(17, 27, 33, .12);
  padding-top: 6px;
}
.agent-preview-replies > div {
  display: flex;
  align-items: flex-start;
  gap: 5px;
  font-size: 12px;
  line-height: 1.45;
}
.agent-preview-replies > div > span:first-child {
  flex: 0 0 auto;
  color: #667781;
  font-weight: 700;
}
.agent-preview-ai-note {
  display: flex;
  align-items: flex-start;
  gap: 5px;
  margin-top: 7px;
  color: #667781;
  font-size: 12px;
  line-height: 1.4;
}
.agent-preview-ai-note i {
  margin-top: 2px;
  color: #e77f25;
}
.agent-preview-time {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  margin-top: 4px;
  color: #667781;
  font-size: 12px;
}
.agent-preview-time .fa {
  color: #53bdeb;
  font-size: 12px;
}
.agent-preview-attachment {
  display: block;
  margin-bottom: 6px;
  overflow: hidden;
  border-radius: 8px;
  background: rgba(255, 255, 255, .55);
  padding: 5px;
  color: #667781;
  font-size: 12px;
}
.agent-preview-attachment img {
  display: block;
  width: 100%;
  max-height: 190px;
  border-radius: 6px;
  object-fit: cover;
}
.agent-preview-file-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 66px;
  border-radius: 6px;
  background: rgba(255, 255, 255, .58);
  padding: 10px;
  color: #667781;
  font-size: 12px;
  text-align: center;
}
.agent-preview-file-placeholder i {
  color: #b45309;
  font-size: 17px;
}
.agent-preview-doc-card {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  border-radius: 6px;
  background: #fff;
  padding: 8px;
}
.agent-preview-doc-card > i {
  flex: 0 0 auto;
  color: #e77f25;
  font-size: 20px;
}
.agent-preview-doc-card.is-empty {
  background: rgba(255, 255, 255, .58);
  color: #667781;
}
.agent-preview-doc-meta {
  min-width: 0;
}
.agent-preview-doc-meta strong {
  display: block;
  color: #111b21;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.35;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.agent-preview-doc-meta span {
  display: block;
  margin-top: 2px;
  color: #667781;
  font-size: 12px;
  line-height: 1.35;
}
.agent-preview-doc-card.is-empty strong {
  color: #667781;
  font-weight: 600;
}
.agent-preview-meta {
  margin-top: 12px;
}
.agent-preview-meta > div {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 7px 0;
  border-top: 1px solid #eef2f6;
  font-size: 12px;
}
.agent-preview-meta span {
  color: #98a2b3;
}
.agent-preview-meta strong {
  color: #344054;
  text-align: right;
}
.agent-preview-variable-note {
  margin-top: 8px;
  color: #98a2b3;
  font-size: 12px;
  line-height: 1.4;
}
@media (max-width: 991px) {
  .agent-form-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .agent-form-preview {
    order: -1;
  }
}
@media (max-width: 575px) {
  .agent-form-two,
  .agent-ai-mode {
    grid-template-columns: minmax(0, 1fr);
  }
  .agent-config-form {
    padding: 14px;
  }
  .agent-section-head {
    gap: 12px;
  }
  .agent-attachment-type {
    display: flex;
    width: 100%;
  }
  .agent-attachment-type-btn {
    flex: 1 1 0;
    min-width: 0;
  }
  .agent-attachment-current {
    align-items: flex-start;
    flex-wrap: wrap;
  }
  .agent-attachment-actions {
    width: 100%;
    justify-content: flex-end;
  }
  .agent-kb-head {
    flex-direction: column;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/ai/agentV2/components/AgentConfigForm.vue